import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Field, FieldArray, reduxForm, formValueSelector } from 'redux-form';
import { Input, requiredInput } from '../../../core/formValidation';
import AddressFields from './AddressFields';
import ManualAddressFields from './ManualAddressFields';
import ProgramAddressFields from './ProgramAddressFields';
import { Collapse } from 'reactstrap';
import { useSelector } from 'react-redux';

const validateNewProducts = (values) => {
  const errors = {};
  if (!values.addressFields) {
    return errors;
  }
  const addressFieldErrors = [];
  values.addressFields.forEach((a, i) => {
    const addressErrors = {};
    if (!a || !a.name) {
      addressErrors.name = 'Required';
      addressFieldErrors[i] = addressErrors;
    }
  });
  if (addressFieldErrors.length) {
    errors.addressFields = addressFieldErrors;
  }
  return errors;
};

const WashCounterForm = ({
  startAddress,
  addressFields,
  products,
  operations,
  change,
  handleSubmit,
  siteSettings,
  siteInstallationGroup,
}) => {
  // context
  const intl = useIntl();
  const formSelector = formValueSelector('washCounter');
  const useMaxRangeManual = useSelector((state) => formSelector(state, 'useMaxRangeManual'));
  const maxRangeManual = useSelector((state) => formSelector(state, 'maxRangeManual'));
  const useMaxRangeProgram = useSelector((state) => formSelector(state, 'useMaxRangeProgram'));
  const maxRangeProgram = useSelector((state) => formSelector(state, 'maxRangeProgram'));
  const manualAddresses = useSelector((state) => formSelector(state, 'manualAddresses')) || [];
  const manualEntriesPrograms =
    useSelector((state) => formSelector(state, 'manualEntriesPrograms')) || [];
  // state
  const [toggle, setToggle] = useState({
    additionalRegisters: false,
    washRegisters: false,
  });

  const findUsableProducts = () => {
    // find products that are not in unique products
    const productsNotInUniqueProducts = products.filter(
      (p) => !uniqueProducts.some((up) => up.id === p.id)
    );
    return productsNotInUniqueProducts;
  };

  // make products unique using state
  const [uniqueProducts, setUniqueProducts] = useState([]);
  // make usable products unique using state
  const [usableProducts, setUsableProducts] = useState([]);
  // on mount, set unique products from address fields
  useEffect(() => {
    if (addressFields && addressFields.length) {
      setUniqueProducts(addressFields.map((a) => ({ id: a.productId, name: a.name })));
    }
  }, [addressFields]);

  useEffect(() => {
    if (uniqueProducts.length) {
      setUsableProducts(findUsableProducts());
    }
  }, [uniqueProducts]);

  useEffect(() => {
    if (useMaxRangeManual && manualAddresses.length > 0) {
      const allAddresses = manualAddresses.map((a) => a?.address).filter(Boolean);
      if (allAddresses.length > 0) {
        const highestAddress = Math.max(...allAddresses);
        const nextAvailableRegister = highestAddress + 1;
        if (!maxRangeManual || maxRangeManual < nextAvailableRegister) {
          change('maxRangeManual', nextAvailableRegister);
        }
      }
    }
  }, [useMaxRangeManual, manualAddresses, maxRangeManual, change]);

  useEffect(() => {
    if (useMaxRangeProgram && manualEntriesPrograms.length > 0) {
      const allAddresses = manualEntriesPrograms.map((a) => a?.address).filter(Boolean);
      if (allAddresses.length > 0) {
        const highestAddress = Math.max(...allAddresses);
        const nextAvailableRegister = highestAddress + 1;
        if (!maxRangeProgram || maxRangeProgram < nextAvailableRegister) {
          change('maxRangeProgram', nextAvailableRegister);
        }
      }
    }
  }, [useMaxRangeProgram, manualEntriesPrograms, maxRangeProgram, change]);

  const handleToggle = (value) => {
    setToggle({ ...toggle, [value]: !toggle[value] });
  };

  const handleProductChange = (event) => {
    event.preventDefault();
    const field = event.target.name.replace('.productId', '');
    const { id } = products.find((p) => p.id === Number(event.target.value));
    change(`${field}.productId`, id);
  };

  const handleProductChangeWithName = (field) => {
    if (field.id != null && !uniqueProducts.some((up) => up.id === field.id)) {
      setUniqueProducts([...uniqueProducts, field]);
      // update usable products using the new unique products
      setUsableProducts(findUsableProducts());
    }
  };

  const handleStartAddressChange = ({ target }) => {
    addressFields.forEach((a, i) => {
      const newAddress = a.index + Number(target.value || 0, 10);
      change(`addressFields[${i}].address`, newAddress);
    });
  };

  const lockedFieldStyle = {
    border: '1.5px solid #000',
  };

  return (
    <form data-testid="wash-counter-form" onSubmit={handleSubmit}>
      <div className="form-row">
        {siteSettings.vpnGroupEnabled &&
        siteInstallationGroup &&
        siteInstallationGroup.vpnGateway &&
        siteInstallationGroup.modbusPortAllocation ? (
          <>
            <div className="form-group">
              <label>
                <i className="icon icon-lock" />
                {intl.formatMessage({ id: 'tommyHost' })}
              </label>
              <Field
                type="text"
                className="form-control"
                style={lockedFieldStyle}
                name="host"
                id="host"
                component={(input) => (
                  <Input
                    {...input}
                    className="form-control"
                    disabled
                    value={siteInstallationGroup?.vpnGateway}
                  />
                )}
              />
            </div>
            <div className="form-group">
              <label>
                <i className="icon icon-lock" />
                {intl.formatMessage({ id: 'tommyModbusPort' })}
              </label>
              <Field
                type="number"
                className="form-control"
                style={lockedFieldStyle}
                name="port"
                id="port"
                component={(input) => (
                  <Input
                    {...input}
                    className="form-control"
                    disabled
                    value={siteInstallationGroup?.modbusPortAllocation?.externalPort}
                  />
                )}
                parse={(value) => (value ? Number(value, 10) : null)}
              />
            </div>
          </>
        ) : (
          <>
            <div className="form-group">
              <label>{intl.formatMessage({ id: 'host' })}</label>
              <Field
                type="text"
                className="form-control"
                name="host"
                id="host"
                component={requiredInput}
              />
            </div>
            <div className="form-group">
              <label>{intl.formatMessage({ id: 'mcpPort' })}</label>
              <Field
                type="number"
                className="form-control"
                name="port"
                id="port"
                component={requiredInput}
                parse={(value) => (value ? Number(value, 10) : null)}
              />
            </div>
          </>
        )}
        <div className="form-group">
          <label>{intl.formatMessage({ id: 'startingAddress' })}</label>
          <Field
            type="number"
            className="form-control"
            name="startAddress"
            id="startAddress"
            component={requiredInput}
            onChangeCapture={(event) => handleStartAddressChange(event, change, addressFields)}
            parse={(value) => (value ? Number(value, 10) : null)}
          />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>{intl.formatMessage({ id: 'carCountAddress' })}</label>
          <Field
            type="number"
            className="form-control"
            name="totalCarCountAddress"
            id="totalCarCountAddress"
            component={requiredInput}
            parse={(value) => (value ? Number(value, 10) : null)}
          />
        </div>
        <div className="form-group">
          <label>{intl.formatMessage({ id: 'lastHourAddress' })}</label>
          <Field
            type="number"
            className="form-control"
            name="lastHourAddress"
            id="lastHourAddress"
            component={requiredInput}
            parse={(value) => (value ? Number(value, 10) : null)}
          />
        </div>
        <div className="form-group">
          <label>{intl.formatMessage({ id: 'currentHourAddress' })}</label>
          <Field
            type="number"
            className="form-control"
            name="currentHourAddress"
            id="currentHourAddress"
            component={requiredInput}
            parse={(value) => (value ? Number(value, 10) : null)}
          />
        </div>
      </div>
      <div onClick={() => handleToggle('additionalRegisters')} className="form-title collapsible">
        <span>{intl.formatMessage({ id: 'additionalRegisters' })}</span>
        <div style={{ border: '0px' }}>
          <i className="icon icon-chevron-down" />
        </div>
      </div>
      <Collapse isOpen={toggle.additionalRegisters}>
        <div className="form-row">
          <div className="form-group">
            <label>{intl.formatMessage({ id: 'lcPort' })}</label>
            <Field
              type="number"
              className="form-control"
              name="lcPort"
              id="lcPort"
              component={Input}
              parse={(value) => (value ? Number(value, 10) : null)}
            />
          </div>
          <div className="form-group">
            <label>{intl.formatMessage({ id: 'nmPort' })}</label>
            <Field
              type="number"
              className="form-control"
              name="nmPort"
              id="nmPort"
              component={Input}
              parse={(value) => (value ? Number(value, 10) : null)}
            />
          </div>
          <div className="form-group">
            <label>{intl.formatMessage({ id: 'vcPort' })}</label>
            <Field
              type="number"
              className="form-control"
              name="vcPort"
              id="vcPort"
              component={Input}
              parse={(value) => (value ? Number(value, 10) : null)}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>{intl.formatMessage({ id: 'rocPort' })}</label>
            <Field
              type="number"
              className="form-control"
              name="rocPort"
              id="rocPort"
              component={Input}
              parse={(value) => (value ? Number(value, 10) : null)}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group mt-3 mb-0">
            <label>{intl.formatMessage({ id: 'manualEntries' })}</label>
          </div>
        </div>
        <div className="form-row mb-0">
          <div className="form-group" style={{ paddingLeft: '20px' }}>
            <label className="form-check-label">
              <Field
                name="useMaxRangeManual"
                id="useMaxRangeManual"
                className="form-check-input"
                component="input"
                type="checkbox"
              />{' '}
              {intl.formatMessage({ id: 'useMaxRange' })}
            </label>
          </div>
        </div>
        {useMaxRangeManual && (
          <div className="form-row mb-2">
            <div className="form-group">
              <label>{intl.formatMessage({ id: 'maxRange' })}</label>
              <Field
                type="number"
                className="form-control"
                name="maxRangeManual"
                id="maxRangeManual"
                component={requiredInput}
                parse={(value) => (value ? Number(value, 10) : null)}
              />
            </div>
          </div>
        )}
        <FieldArray
          name="manualAddresses"
          component={ManualAddressFields}
          products={products}
          intl={intl}
          handleProductChange={(event) => handleProductChange(event, change, products)}
          useMaxRange={useMaxRangeManual}
          maxRange={maxRangeManual}
        />
        <div className="form-row">
          <div className="form-group mt-3 mb-0">
            <label>{intl.formatMessage({ id: 'manualEntriesPrograms' })}</label>
          </div>
        </div>
        <div className="form-row mb-0">
          <div className="form-group" style={{ paddingLeft: '20px' }}>
            <label className="form-check-label">
              <Field
                name="useMaxRangeProgram"
                id="useMaxRangeProgram"
                className="form-check-input"
                component="input"
                type="checkbox"
              />{' '}
              {intl.formatMessage({ id: 'useMaxRange' })}
            </label>
          </div>
        </div>
        {useMaxRangeProgram && (
          <div className="form-row mb-2">
            <div className="form-group">
              <label>{intl.formatMessage({ id: 'maxRange' })}</label>
              <Field
                type="number"
                className="form-control"
                name="maxRangeProgram"
                id="maxRangeProgram"
                component={requiredInput}
                parse={(value) => (value ? Number(value, 10) : null)}
              />
            </div>
          </div>
        )}
        <FieldArray
          name="manualEntriesPrograms"
          component={ProgramAddressFields}
          intl={intl}
          handleProductChange={(event) => handleProductChange(event, change, products)}
          useMaxRange={useMaxRangeProgram}
          maxRange={maxRangeProgram}
        />
      </Collapse>
      <div onClick={() => handleToggle('washRegisters')} className="form-title collapsible">
        <span>{intl.formatMessage({ id: 'washRegisters' })}</span>
        <div style={{ border: '0px' }}>
          <i className="icon icon-chevron-down" />
        </div>
      </div>
      <Collapse isOpen={toggle.washRegisters}>
        <FieldArray
          name="addressFields"
          component={AddressFields}
          startAddress={startAddress}
          remainingProducts={usableProducts}
          products={products}
          operations={operations}
          handleProductChange={(event) => handleProductChangeWithName(event, change, products)}
        />
      </Collapse>
    </form>
  );
};

export const WashControllerConfigForm = reduxForm({
  form: 'washCounter',
  validate: validateNewProducts,
})(WashCounterForm);
